/*
  <kg-localization-auto>: stellt beim ersten Besuch einmalig das Lieferland
  auf Shopifys Vorschlag um (Land aus IP und Browsersprache), wenn es sich
  vom aktuellen Land unterscheidet und im Shop verfuegbar ist. Danach zeigt
  ein Toast den Wechsel mit der Moeglichkeit, ihn zu aendern. Eine manuelle
  Wahl im Laender-Drawer wird gemerkt und nie wieder ueberschrieben.

  Zum Testen in der Konsole:
    document.querySelector('kg-localization-auto').apply('AT')  Wechsel auf AT mit Toast
    document.querySelector('kg-localization-auto').reset()      Merker loeschen, danach neu laden
*/

const root = () => window.Shopify?.routes?.root || '/';
const STORE_KEY = 'kgroup:market';
const TOAST_KEY = 'kgroup:market-toast';
const RECHECK_AFTER = 1000 * 60 * 60 * 24 * 30;

function read(storage, key) {
  try {
    return JSON.parse(storage.getItem(key));
  } catch {
    return null;
  }
}

function write(storage, key, value) {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/*
  Laesst sich ueberhaupt etwas merken? In einem privaten Tab und bei blockierten Website-Daten
  wirft schon der Zugriff auf localStorage. Das ist hier nicht nur ein Schoenheitsfehler: Ohne
  Merker weiss der naechste Seitenaufruf nichts von einer Wahl von Hand, und der Vorschlag aus
  der IP stellt sie wieder zurueck — ein Wechsel von Hand waere damit verloren.
*/
function speicherGeht(storage) {
  const probe = 'kgroup:probe';
  if (!write(storage, probe, 1)) return false;
  write(storage, probe, null);
  return true;
}

class KgLocalizationAuto extends HTMLElement {
  connectedCallback() {
    this.country = this.dataset.country || window.Shopify?.country;
    this.language = this.dataset.language || window.Shopify?.language;
    this.countries = (this.dataset.countries || '').split(',').map((code) => code.trim()).filter(Boolean);
    this.names = read({ getItem: () => this.dataset.names }, 'names') || {};

    /*
      Die Wahl von Hand kommt in beide Speicher. localStorage ist der eigentliche Ort, aber er
      wird auch mal geleert; sessionStorage haelt dann wenigstens den laufenden Besuch zusammen.
    */
    const merkeWahl = () => {
      const stand = { manual: true, at: Date.now() };
      write(localStorage, STORE_KEY, stand);
      write(sessionStorage, STORE_KEY, stand);
    };
    const form = document.getElementById(this.dataset.form);
    form?.addEventListener('submit', merkeWahl);

    this.showPendingToast();
    if (this.shouldSkip()) return;
    window.setTimeout(() => this.check(), 300);
  }

  shouldSkip() {
    if (window.Shopify?.designMode || navigator.webdriver) return true;
    if (/bot|crawl|spider|slurp|lighthouse|headless/i.test(navigator.userAgent)) return true;
    /*
      Was nicht gemerkt werden kann, darf nicht getan werden: Ohne Speicher wuerde der Wechsel
      bei jedem Aufruf neu zuschlagen und jede Wahl von Hand ueberschreiben. Lieber gar kein
      automatischer Wechsel als einer, der sich nicht abstellen laesst.
    */
    if (!speicherGeht(localStorage)) return true;
    const stored = read(localStorage, STORE_KEY) || read(sessionStorage, STORE_KEY);
    if (stored?.manual) return true;
    if (stored?.checked && Date.now() - stored.checked < RECHECK_AFTER) return true;
    return false;
  }

  async check() {
    try {
      const url = `${root()}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${encodeURIComponent(this.country || '')}`;
      const data = await (await fetch(url, { headers: { Accept: 'application/json' } })).json();
      const suggestion = data?.suggestions?.[0]?.parts?.country || data?.detected_values?.country;
      const code = suggestion?.handle;
      write(localStorage, STORE_KEY, { checked: Date.now(), suggested: code || null });
      if (!code || code === this.country || !this.countries.includes(code)) return;
      this.apply(code, suggestion.name);
    } catch {
      /* Kein Vorschlag, Kontext bleibt */
    }
  }

  /* Lieferland umstellen: Toast fuer die naechste Seite vormerken und das Localization-Formular absenden. */
  apply(code, name) {
    if (!code) return;
    write(sessionStorage, TOAST_KEY, { code, name: name || this.names[code] || code });
    const form = document.createElement('form');
    form.method = 'post';
    form.action = `${root()}localization`;
    form.hidden = true;
    const fields = { form_type: 'localization', utf8: '✓', _method: 'put', country_code: code, return_to: window.location.pathname + window.location.search };
    Object.entries(fields).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.append(input);
    });
    document.body.append(form);
    form.submit();
  }

  showPendingToast() {
    const pending = read(sessionStorage, TOAST_KEY);
    if (!pending) return;
    write(sessionStorage, TOAST_KEY, null);
    const name = this.names[this.country] || pending.name || this.country;
    const message = (this.dataset.message || '').replace('[land]', name);
    const open = () => document.getElementById(this.dataset.drawer)?.open?.();
    const show = () => window.kgToast?.(message, this.dataset.action, open, 10000);
    if (window.kgToast) show();
    else window.addEventListener('load', () => window.setTimeout(show, 100), { once: true });
  }

  reset() {
    write(localStorage, STORE_KEY, null);
    write(sessionStorage, TOAST_KEY, null);
  }
}

customElements.define('kg-localization-auto', KgLocalizationAuto);
